import type { TFunction } from "i18next";
import type {
  UnitManagementLookupItem,
  VehicleDetailResponse,
  VehicleSearchItem,
} from "src/services/unitManagementService";

export const MIN_SEARCH_LENGTH = 3;

export type UnitManagementOption = {
  label: string;
  value: string | number;
};

export const getFieldValue = (
  item: UnitManagementLookupItem,
): string | number | undefined => {
  const value = item.id ?? item.value;
  return typeof value === "string" || typeof value === "number"
    ? value
    : undefined;
};

export const getItemLabel = (item: UnitManagementLookupItem) => {
  const surname = typeof item.surname === "string" ? item.surname : "";
  const fullName = [item.name, surname].filter(Boolean).join(" ");
  const fallback =
    item.companyName ||
    item.label ||
    item.title ||
    fullName ||
    item.code ||
    item.imei ||
    item.serial;

  return String(fallback ?? "");
};

export const getUnitLabel = (item: UnitManagementLookupItem) => {
  const imei = item.imei ? `IMEI: ${item.imei}` : "";
  const serial = item.serial ? `Serial: ${item.serial}` : "";
  const label = [imei, serial].filter(Boolean).join(" - ");

  return label || getItemLabel(item);
};

const pickArray = (value: unknown): UnitManagementLookupItem[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is UnitManagementLookupItem =>
      !!item && typeof item === "object",
  );
};

export const extractLookupItems = (value: unknown): UnitManagementLookupItem[] => {
  if (Array.isArray(value)) {
    const first = value[0];

    if (first && typeof first === "object") {
      const nested = first as Record<string, unknown>;
      const nestedResponse = pickArray(nested.response);

      if (nestedResponse.length > 0) return nestedResponse;
    }

    return pickArray(value);
  }

  if (!value || typeof value !== "object") return [];

  const record = value as Record<string, unknown>;
  const responseItems = pickArray(record.response);
  if (responseItems.length > 0) return responseItems;

  const innerResponse = record.response;
  if (innerResponse && typeof innerResponse === "object" && !Array.isArray(innerResponse)) {
    const nestedItems = pickArray(
      (innerResponse as Record<string, unknown>).response,
    );
    if (nestedItems.length > 0) return nestedItems;
  }

  const dataItems = pickArray(record.data);
  if (dataItems.length > 0) return dataItems;

  const speedItems = pickArray(record.speeds);
  if (speedItems.length > 0) return speedItems;

  return pickArray(record.content);
};

export const extractVehicleSearchItems = (value: unknown): VehicleSearchItem[] =>
  extractLookupItems(value) as VehicleSearchItem[];

export const toOptions = (items: UnitManagementLookupItem[]): UnitManagementOption[] =>
  items
    .map((item) => {
      const value = getFieldValue(item);
      const label = getItemLabel(item);

      if (value === undefined || !label) return null;

      return { value, label };
    })
    .filter((option): option is UnitManagementOption => !!option);

export const toUnitOptions = (items: UnitManagementLookupItem[]): UnitManagementOption[] =>
  items.reduce<UnitManagementOption[]>((options, item) => {
    const value = getFieldValue(item);
    const label = getUnitLabel(item);

    if (value === undefined || !label) return options;

    return [...options, { value, label }];
  }, []);

export const toVehicleOptions = (
  items: VehicleSearchItem[],
  t: TFunction,
): UnitManagementOption[] =>
  items.reduce<UnitManagementOption[]>((options, item) => {
    const value = getFieldValue(item);
    if (value === undefined) return options;

    return [
      ...options,
      {
        value,
        label: getVehicleOptionLabel(item, t),
      },
    ];
  }, []);

export const getVehicleOptionLabel = (vehicle: VehicleSearchItem, t: TFunction) =>
  `${t("license_plate")}: ${vehicle.licensePlate ?? ""} - ${t("vin")}: ${vehicle.vin ?? ""}`;

export const getQueryData = (value: unknown) => {
  if (!value || typeof value !== "object" || !("data" in value)) {
    return undefined;
  }

  return (value as { data?: unknown }).data;
};

export const numberOrUndefined = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getDeviceSerialFromVehicle = (
  vehicle: VehicleDetailResponse | null | undefined,
): string => {
  if (!vehicle) return "";

  const device = vehicle.device;

  if (typeof vehicle.serial === "string" && vehicle.serial) {
    return vehicle.serial;
  }

  if (device && typeof device.serial === "string" && device.serial) {
    return device.serial;
  }

  if (device?.unit && typeof device.unit.serial === "string" && device.unit.serial) {
    return device.unit.serial;
  }

  if (vehicle.unit && typeof vehicle.unit.serial === "string" && vehicle.unit.serial) {
    return vehicle.unit.serial;
  }

  return "";
};

export const getSerialFromLookupItem = (item: UnitManagementLookupItem | null) => {
  if (!item) return "";
  return typeof item.serial === "string" ? item.serial : "";
};

export const getFuelSensorSerialLabel = (
  row: { unit?: { imei?: string; serial?: string } },
) => {
  const imei = row.unit?.imei ? `IMEI: ${row.unit.imei}` : "";
  const serial = row.unit?.serial ? `Serial: ${row.unit.serial}` : "";
  return [imei, serial].filter(Boolean).join(" - ") || "—";
};
